import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ExternalLink, Newspaper, Bookmark } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ScoreBadge, SourceTag, formatDate, daysUntil } from "../lib/format";

export default function Dashboard() {
  const { activeCompany } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);
  const [newsMeta, setNewsMeta] = useState({ last_ingested_at: null, refreshing: false });
  const [tracked, setTracked] = useState([]);
  const [profileCompleted, setProfileCompleted] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([
      api.get("/admin/stats").then((r) => r.data).catch(() => null),
      api.get("/news/uk-defence", { params: { limit: 12 } }).then((r) => r.data).catch(() => ({ items: [] })),
      api.get("/tracked").then((r) => r.data).catch(() => []),
      api.get("/profile").then((r) => r.data).catch(() => null),
    ]).then(([s, n, t, p]) => {
      if (cancel) return;
      setStats(s);
      setNews(n?.items || []);
      setNewsMeta({
        last_ingested_at: n?.last_ingested_at || null,
        refreshing: !!n?.refreshing,
      });
      setTracked(Array.isArray(t) ? t : []);
      setProfileCompleted(p ? !!p.completed : true);
      setLoading(false);
    });
    return () => { cancel = true; };
  }, [activeCompany?.id]);

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-meta">Workspace</div>
          <div className="display">{activeCompany?.name}</div>
        </div>
        <div className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>
          Plan · <span className="value-mono">{(activeCompany?.plan || "standard").toUpperCase()}</span>
        </div>
      </div>

      {/* Banner for users who skipped onboarding */}
      {!loading && !profileCompleted && (
        <div
          className="p-3 mb-6 flex items-center justify-between"
          style={{ background: "#FFF8E5", border: "1px solid #F2C94C", borderRadius: 4 }}
          data-testid="profile-incomplete-banner"
        >
          <div className="text-[13px]">
            <strong>Finish your profile</strong> to unlock accurate Fit Scoring on 1000s of live opportunities.
          </div>
          <Link
            to="/onboarding"
            className="text-[12px] underline"
            data-testid="profile-incomplete-cta"
          >
            Complete now →
          </Link>
        </div>
      )}

      {/* Total opportunities hero */}
      <Link to="/app/opportunities" className="block mb-8" data-testid="total-opportunities-tile">
        <div className="p-card hover:shadow-sm transition" style={{ borderColor: "var(--parapet-border)" }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="label-meta">Total opportunities</div>
              <div className="display" style={{ fontSize: 56, lineHeight: 1.05, marginTop: 4 }}>
                {loading ? "…" : (stats?.notices_total ?? 0).toLocaleString()}
              </div>
              <div className="text-[12px] mt-2" style={{ color: "var(--parapet-text-secondary)" }}>
                {stats?.recent_7d ?? 0} added in the last 7 days · Live across UK / EU / US sources
              </div>
            </div>
            <div className="text-[12px] underline">Browse all notices →</div>
          </div>
        </div>
      </Link>

      <div className="grid grid-cols-3 gap-4">
        {/* UK defence news ticker */}
        <div className="p-card col-span-2" data-testid="uk-defence-news-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Newspaper size={16} />
              <h2 className="m-0">Latest defence news</h2>
            </div>
            <span className="text-[11px] value-mono" style={{ color: "var(--parapet-text-secondary)" }} data-testid="news-meta">
              {newsMeta.refreshing
                ? "refreshing…"
                : (newsMeta.last_ingested_at ? `updated ${formatRelativeShort(newsMeta.last_ingested_at)}` : `${news.length} headlines`)}
            </span>
          </div>
          {loading ? (
            <span className="spinner" />
          ) : news.length === 0 ? (
            <div className="empty-state">
              <h3>No news ingested yet</h3>
              <p>Trade press feeds (UKDJ, Defence Online, Shephard, ADS) run daily.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="news-list">
              {news.map((n, i) => <NewsRow key={n.url || n.title || `news-${i}`} item={n} idx={i} />)}
            </ul>
          )}
        </div>

        {/* Tracked tenders */}
        <div className="p-card" data-testid="tracked-tenders-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bookmark size={16} />
              <h2 className="m-0">My tracked tenders</h2>
            </div>
            <Link to="/app/tracked" className="text-[12px] underline">View all →</Link>
          </div>
          {loading ? (
            <span className="spinner" />
          ) : tracked.length === 0 ? (
            <div className="empty-state">
              <h3>Nothing tracked yet</h3>
              <p className="text-[12px]">
                Open a notice and click <span className="value-mono">Track</span> to add it here.
              </p>
              <Link to="/app/opportunities" className="btn-primary mt-3 inline-block" data-testid="empty-track-cta">
                Browse opportunities →
              </Link>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="tracked-list">
              {tracked.slice(0, 8).map((t, i) => (
                <TrackedRow key={t.notice?.id || i} item={t} navigate={navigate} />
              ))}
              {tracked.length > 8 && (
                <li className="text-[12px] pt-1" style={{ color: "var(--parapet-text-secondary)" }}>
                  + {tracked.length - 8} more · <Link to="/app/tracked" className="underline">see all</Link>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function NewsRow({ item, idx }) {
  const dateStr = useMemo(() => formatPublishedDate(item.published_at), [item.published_at]);
  return (
    <li
      className="flex items-start gap-3 pb-3"
      style={{ borderBottom: "1px solid var(--parapet-grid)" }}
      data-testid={`news-item-${idx}`}
    >
      <div className="value-mono text-[11px] pt-[3px]" style={{ color: "var(--parapet-text-secondary)", minWidth: 64 }}>
        {dateStr}
      </div>
      <div className="flex-1 min-w-0">
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] font-medium hover:underline block"
          data-testid={`news-link-${idx}`}
        >
          {item.title}
        </a>
        <div className="text-[11px] mt-1 flex items-center gap-2" style={{ color: "var(--parapet-text-secondary)" }}>
          <span className="value-mono">{item.publisher}</span>
          {item.url && <ExternalLink size={11} />}
        </div>
      </div>
    </li>
  );
}

function TrackedRow({ item, navigate }) {
  const n = item.notice || {};
  const total = item.checklist_total || 0;
  const done = item.checklist_completed || 0;
  return (
    <li
      className="cursor-pointer pb-3"
      style={{ borderBottom: "1px solid var(--parapet-grid)" }}
      onClick={() => navigate(`/app/opportunities/${n.id}`)}
      data-testid={`tracked-item-${n.id}`}
    >
      <div className="flex items-start gap-2">
        <ScoreBadge score={item.fit?.score} band={item.fit?.band} insufficient={item.fit?.insufficient_data} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium truncate">{n.title || "(Untitled)"}</div>
          <div className="mt-1 flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
            <SourceTag source={n.source} url={n.source_url} />
            {n.deadline && (
              <span className="text-[11px]" style={{ color: "var(--parapet-text-secondary)" }}>
                {formatDate(n.deadline)}
                {daysUntil(n.deadline) != null && (
                  <span className="value-mono ml-1">
                    {daysUntil(n.deadline) > 0
                      ? `· ${daysUntil(n.deadline)}d`
                      : daysUntil(n.deadline) === 0
                      ? "· today"
                      : "· passed"}
                  </span>
                )}
              </span>
            )}
          </div>
          {total > 0 && (
            <div className="text-[11px] mt-1 value-mono" style={{ color: "var(--parapet-text-secondary)" }}>
              Checklist {done}/{total}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function formatPublishedDate(raw) {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return String(raw).slice(0, 10);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffH = Math.floor(diffMs / 3600000);
  if (diffH < 1) return "just now";
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

// Compact relative time for the "updated 2h ago" indicator on the news card.
// Keeps the existing publishing time helper above untouched.
function formatRelativeShort(raw) {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "—";
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 2) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
